/* eslint-disable max-classes-per-file */
/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  OnInit,
  booleanAttribute,
} from '@angular/core';
import { Nullable } from '@core/types/nullable.type';
import { ChipBaseComponent } from '../../chip-base/chip-base.component';
import { CHIP } from '../../../variables/chip.variable';

/** Event object emitted by ChipOption when selected or deselected. */
export class ChipSelectionChange {
  constructor(
    /** Reference to the chip that emitted the event. */
    public source: ChipOptionComponent,
    /** Whether the chip that emitted the event is selected. */
    public selected: boolean,
    /** Whether the selection change was a result of a user interaction. */
    public isUserInput = false,
  ) {}
}

/**
 * An extension of the Chip component that supports chip selection. Used with ChipOptionGroup.
 *
 * Unlike other chips, the user can focus on disabled chip options inside a ChipOptionGroup. The
 * user cannot click disabled chips.
 */
@Component({
  selector: 'core-chip-option, [coreChipOption]',
  templateUrl: './chip-option.component.html',
  styleUrls: ['./chip-option.component.scss', '../../chip-base/chip-base.component.scss'],
  host: {
    class: 'chip-option',
    '[class.chip-option--multiple]': 'chipListMultiple',
    '[class.chip-option--disabled]': 'disabled',
    '[class.chip-option--selected]': 'selected',
    '[class.chip-option--highlighted]': 'highlighted',
    '[attr.tabindex]': 'null',
    '[attr.aria-label]': 'null',
    '[attr.aria-description]': 'null',
    '[attr.role]': 'role',
    '[id]': 'id',
  },
  providers: [
    { provide: ChipBaseComponent, useExisting: ChipOptionComponent },
    { provide: CHIP, useExisting: ChipOptionComponent },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipOptionComponent extends ChipBaseComponent implements OnInit {
  /**
   * Whether the chip list is selectable
   */
  chipListSelectable: boolean = true;

  /**
   * Whether the chip list is in multi-selection mode
   */
  chipListMultiple: boolean = false;

  /**
   * Whether or not the chip is selectable.
   *
   * When a chip is not selectable, changes to its selected state are always
   * ignored. By default an option chip is selectable, and it becomes
   * non-selectable if its parent chip list is not selectable.
   */
  @Input({ transform: booleanAttribute })
  get selectable(): boolean {
    return this._selectable && this.chipListSelectable;
  }

  set selectable(value: boolean) {
    this._selectable = value;
    this.changeDetectorRef.markForCheck();
  }

  protected override _selectable: boolean = true;

  /**
   * Whether the chip is selected
   */
  @Input({ transform: booleanAttribute })
  get selected(): boolean {
    return this._selected;
  }

  set selected(value: boolean) {
    this.setSelectedState(value, false, true);
  }

  protected override _selected = false;

  /**
   * The ARIA selected applied to the chip. Conforms to WAI ARIA best practices for listbox
   * interaction patterns.
   *
   * From [WAI ARIA Listbox authoring practices guide](
   * https://www.w3.org/WAI/ARIA/apg/patterns/listbox/):
   *  "If any options are selected, each selected option has either aria-selected or aria-checked
   *  set to true. All options that are selectable but not selected have either aria-selected or
   *  aria-checked set to false."
   *
   * Set `aria-selected="false"` on not-selected listbox options that are selectable to fix
   * VoiceOver reading every option as "selected" (#25736).
   */
  get ariaSelected(): Nullable<string> {
    return this.selectable ? this.selected.toString() : null;
  }

  /**
   * Emitted when the chip is selected or deselected
   */
  @Output() readonly selectionChange: EventEmitter<ChipSelectionChange> = new EventEmitter<ChipSelectionChange>();

  ngOnInit() {
    this.role = 'presentation';
  }

  /**
   * Selects the chip
   */
  select(): void {
    this.setSelectedState(true, false, true);
  }

  /**
   * Deselects the chip
   */
  deselect(): void {
    this.setSelectedState(false, false, true);
  }

  /**
   * Selects this chip and emits userInputSelection event
   */
  selectViaInteraction(): void {
    this.setSelectedState(true, true, true);
  }

  /**
   * Toggles the current selected state of this chip
   */
  toggleSelected(isUserInput: boolean = false): boolean {
    this.setSelectedState(!this.selected, isUserInput, true);

    return this.selected;
  }

  override handlePrimaryActionInteraction(): void {
    if (!this.disabled) {
      // Interacting with the primary action implies that the chip already has focus, however
      // there's a bug in Safari where focus ends up lingering on the previous chip (see #27544).
      // We work around it by explicitly focusing the primary action of the current chip.
      this.focus();

      if (this.selectable) {
        this.toggleSelected(true);
      }
    }
  }

  setSelectedState(isSelected: boolean, isUserInput: boolean, emitEvent: boolean): void {
    if (isSelected !== this.selected) {
      this._selected = isSelected;

      if (emitEvent) {
        this.selectionChange.emit({
          source: this,
          isUserInput,
          selected: this.selected,
        });
      }

      this.changeDetectorRef.markForCheck();
    }
  }
}
